import React, { useState, useEffect } from 'react';
import { X, FileText, CheckCircle } from 'lucide-react';
import axios from 'axios';
import ConfirmationModal from './ConfirmationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgreementModal({ leadId, onClose, onSuccess }) {
  const [lead, setLead] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [validityDays, setValidityDays] = useState(30);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    fetchLeadAndProposal();
  }, [leadId]);

  const fetchLeadAndProposal = async () => {
    try {
      setPreviewLoading(true);
      
      // Fetch lead
      const leadResponse = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      if (leadResponse.ok) {
        const leadData = await leadResponse.json();
        setLead(leadData);
      }

      // Fetch approved proposal
      const proposalResponse = await fetch(`${BACKEND_URL}/api/proposals?lead_id=${leadId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      if (proposalResponse.ok) {
        const proposals = await proposalResponse.json();
        const approvedProposal = proposals.find(p => p.status === 'approved');
        if (approvedProposal) {
          setProposal(approvedProposal);
        } else {
          alert('No approved proposal found for this lead.');
          onClose();
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load proposal data');
      onClose();
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmClick = () => {
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/agreements`, {
        lead_id: leadId,
        validity_days: validityDays
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      console.log('Agreement created:', response.data);
      alert('Agreement created successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating agreement:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create agreement';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (previewLoading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" style={{ maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
            <p>Loading proposal data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!proposal) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate Agreement</h2>
          <button onClick={onClose} className="icon-button">
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Information Banner */}
          <div style={{ 
            padding: '15px', 
            background: '#eff6ff', 
            border: '1px solid #3b82f6', 
            borderRadius: '8px', 
            marginBottom: '20px',
            display: 'flex',
            gap: '10px'
          }}>
            <CheckCircle size={20} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '14px', color: '#1e40af' }}>
              <strong>Agreement will be created from approved proposal</strong>
              <p style={{ margin: '5px 0 0 0', fontSize: '13px' }}>All service details, pricing, and terms will be automatically copied from proposal <strong>{proposal.proposal_number}</strong>. You cannot edit these details.</p>
            </div>
          </div>

          {/* Lead Details */}
          {lead && (
            <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '14px', marginBottom: '10px', color: '#666', fontWeight: '600' }}>Client Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                <div><strong>Name:</strong> {lead.name}</div>
                <div><strong>Email:</strong> {lead.email}</div>
                <div><strong>Phone:</strong> {lead.phone}</div>
                <div><strong>Service:</strong> {lead.service_type}</div>
                {lead.destination_country && <div><strong>Destination:</strong> {lead.destination_country}</div>}
                {lead.travel_date && <div><strong>Travel Date:</strong> {lead.travel_date}</div>}
              </div>
            </div>
          )}

          {/* Services Preview (Read-only) */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', marginBottom: '10px', color: '#666', fontWeight: '600' }}>Services & Deliverables (From Proposal)</h3>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Service</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '80px' }}>Qty</th>
                    <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', width: '120px' }}>Rate</th>
                    <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', width: '120px' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {proposal.line_items.map((item, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '10px' }}>
                        <div style={{ fontWeight: '500' }}>{item.service_name}</div>
                        {item.description && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{item.description}</div>}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{item.quantity}</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>₹{item.rate.toFixed(2)}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '500' }}>₹{item.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pricing Summary (Read-only) */}
          <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px' }}>
              <span>Subtotal:</span>
              <strong>₹{proposal.subtotal.toFixed(2)}</strong>
            </div>

            {proposal.discount_amount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px' }}>
                <span>Discount ({proposal.discount_percentage}%):</span>
                <span>₹{proposal.discount_amount.toFixed(2)}</span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '10px', borderTop: '2px solid #777db0', fontSize: '16px' }}>
              <strong>Total Amount:</strong>
              <strong style={{ color: '#777db0' }}>₹{proposal.total_amount.toFixed(2)}</strong>
            </div>
          </div>

          {/* Agreement Terms Info */}
          <div style={{ padding: '15px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <FileText size={20} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '13px', color: '#92400e' }}>
                <strong>Agreement Terms & Conditions</strong>
                <p style={{ margin: '5px 0 0 0' }}>Will be automatically fetched from the service configuration for {lead?.service_type}.</p>
              </div>
            </div>
          </div>

          {/* Validity Configuration */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Agreement Validity (Days)</label>
            <input
              type="number"
              value={validityDays}
              onChange={(e) => setValidityDays(parseInt(e.target.value) || 30)}
              className="form-input"
              min="1"
              max="365"
              style={{ width: '150px' }}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '5px' }}>Agreement will be valid for {validityDays} days from creation date.</p>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={loading}>
            Cancel
          </button>
          <button onClick={handleConfirmClick} className="btn-primary" disabled={loading}>
            {loading ? 'Creating Agreement...' : 'Create Agreement'}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        title="Create Agreement?"
        message="This will create an agreement from the approved proposal. All service details and pricing from the proposal will be used."
        type="info"
        confirmText="Create Agreement"
        cancelText="Cancel"
      />
    </div>
  );
}
